import { useState } from "react";
import { useSearchParams, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthShell from "@/components/AuthShell";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { resetPassword } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return <Navigate to="/login?error=missing_code" replace />;
  }

  if (done) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please request a new reset link.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "mt-2 h-11 bg-white";

  return (
    <AuthShell title="Choose a new password" subtitle="Use at least 8 characters." testId="reset-password-page">
      {error && (
        <div className="border-l-2 border-red-500 bg-red-50 text-red-700 text-sm px-4 py-3 mb-6" data-testid="reset-password-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Label className="text-sm text-zinc-700">New password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
            placeholder="At least 8 characters"
            minLength={8}
            required
            data-testid="reset-password-input"
          />
        </div>
        <div>
          <Label className="text-sm text-zinc-700">Confirm password</Label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputClass}
            placeholder="Re-enter your password"
            minLength={8}
            required
            data-testid="reset-password-confirm-input"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full h-11 rounded-md bg-[#303030] hover:bg-[#1A1A1A] text-white font-medium transition-colors disabled:opacity-60"
          data-testid="reset-password-submit-btn"
        >
          {submitting ? "Please wait…" : "Set new password"}
        </button>
      </form>

      <p className="mt-8 text-sm">
        <Link to="/login" className="font-medium text-zinc-900 underline underline-offset-4 decoration-zinc-300 hover:decoration-zinc-900">
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
};

export default ResetPasswordPage;
